import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Mail, 
  Lock, 
  User,
  Eye,
  EyeOff,
  Loader2,
  Plane
} from 'lucide-react';
import { toast } from 'sonner';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

// Panchi Bird Logo
const PanchiBirdLogo = ({ className = "w-12 h-12" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_panchi-travel-1/artifacts/5hhgrsb0_20260119_170240_0000.png"
    alt="Panchi"
    className={className}
    style={{ objectFit: 'contain' }}
  />
);

// Floating travel elements
const FloatingElement = ({ children, className, delay = 0 }) => (
  <motion.div
    className={`absolute ${className}`}
    animate={{ y: [0, -15, 0] }}
    transition={{ duration: 4, repeat: Infinity, delay, ease: "easeInOut" }}
  >
    {children}
  </motion.div>
);

export default function Auth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectPath = searchParams.get('redirect') || '/';
  const { signInWithEmail, signUpWithEmail, setUser, loading } = useAuth();
  
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    try {
      if (isLogin) {
        await signInWithEmail(email, password);
        toast.success('Welcome back! ✈️');
      } else {
        await signUpWithEmail(email, password, name);
        toast.success('Account created! 🎉');
      }
      // Redirect to intended destination or home
      navigate(redirectPath);
    } catch (error) {
      toast.error(error.message || 'Authentication failed');
    }
  };
  
  const handleGoogleSuccess = async (credentialResponse) => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    try {
      setGoogleLoading(true);
      
      // Send credential to backend for verification
      const response = await api.verifyGoogleToken(credentialResponse.credential);
      
      if (response.success) {
        // Set user in auth context
        setUser({
          user_id: response.user_id,
          email: response.email,
          name: response.name,
          picture: response.picture,
          locale: response.locale,
          sessionToken: response.session_token
        });
        
        toast.success(response.is_new_user ? 'Account created! 🎉' : 'Welcome back! ✈️');
        
        // Redirect to onboarding for new users, otherwise to intended destination
        if (response.is_new_user) {
          navigate('/onboarding');
        } else {
          navigate(redirectPath);
        }
      }
    } catch (error) {
      console.error('Google sign-in error:', error);
      const errorMessage = error?.message || error?.detail || 'Google sign-in failed. Please try again.';
      toast.error(errorMessage);
    } finally {
      setGoogleLoading(false);
    }
  };
  
  const handleGoogleError = (error) => {
    console.error('Google OAuth error:', error);
    // Don't show error for postMessage cloning issue - it's a known Google OAuth quirk
    // that usually resolves on retry
    if (error?.message?.includes('postMessage') || error?.message?.includes('cloned')) {
      console.log('Ignoring postMessage error - will work on retry');
      return;
    }
    toast.error('Google sign-in was cancelled or failed. Please try again.');
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-teal-50 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 -left-20 w-72 h-72 bg-teal-200/40 blur-3xl" style={{ borderRadius: '50%' }} />
        <div className="absolute bottom-20 -right-20 w-96 h-96 bg-cyan-200/40 blur-3xl" style={{ borderRadius: '50%' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-amber-100/30 blur-3xl" style={{ borderRadius: '50%' }} />
        
        {/* Floating travel icons */}
        <FloatingElement className="top-20 left-10 text-4xl opacity-60" delay={0}>✈️</FloatingElement>
        <FloatingElement className="top-32 right-20 text-3xl opacity-60" delay={1}>🌍</FloatingElement>
        <FloatingElement className="bottom-32 left-20 text-3xl opacity-60" delay={0.5}>🧳</FloatingElement>
        <FloatingElement className="bottom-20 right-10 text-4xl opacity-60" delay={1.5}>🏝️</FloatingElement>
      </div>
      
      {/* Back Button */}
      <Link 
        to="/"
        className="absolute top-6 left-6 flex items-center gap-2 text-gray-500 hover:text-teal-600 transition-colors z-20"
        data-testid="back-home"
      >
        <ArrowLeft size={20} />
        <span>Home</span>
      </Link>
      
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Logo & Header */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring' }}
            className="inline-block"
          >
            <PanchiBirdLogo className="w-16 h-16 mx-auto" />
          </motion.div>
          <h1 className="text-3xl md:text-4xl font-black text-gray-900 mt-6 mb-2">
            {isLogin ? 'Welcome Back' : 'Join Panchi'}
          </h1>
          <p className="text-gray-500">
            {isLogin ? 'Sign in to continue planning' : 'Start your journey with AI'}
          </p>
        </div>
        
        {/* Auth Card */}
        <div className="p-6 md:p-8 bg-white/80 backdrop-blur-xl border border-white/50 shadow-xl" style={{ borderRadius: '1.5rem' }}>
          {/* Google Sign In */}
          <div className="flex justify-center mb-6">
            {googleLoading ? (
              <div className="flex items-center justify-center gap-3 py-4 px-6 bg-gray-100 text-gray-600" style={{ borderRadius: '9999px' }}>
                <Loader2 size={20} className="animate-spin" />
                <span>Signing in...</span>
              </div>
            ) : (
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                theme="outline"
                size="large"
                width="320"
                text="continue_with"
                shape="pill"
                logo_alignment="center"
              />
            )}
          </div>
          
          {/* Divider */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-gray-400 text-sm">or</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>
          
          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div className="relative">
                <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-2 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-teal-500 focus:bg-white outline-none transition-all"
                  style={{ borderRadius: '9999px' }}
                  data-testid="name-input"
                />
              </div>
            )}
            
            <div className="relative">
              <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address"
                className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-2 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-teal-500 focus:bg-white outline-none transition-all"
                style={{ borderRadius: '9999px' }}
                data-testid="email-input"
              />
            </div>
            
            <div className="relative">
              <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="w-full pl-12 pr-12 py-3.5 bg-gray-50 border-2 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-teal-500 focus:bg-white outline-none transition-all"
                style={{ borderRadius: '9999px' }}
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-bold shadow-lg shadow-teal-500/30 hover:shadow-teal-500/50 hover:scale-[1.02] transition-all disabled:opacity-50"
              style={{ borderRadius: '9999px' }}
              data-testid="submit-btn"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 size={18} className="animate-spin" />
                  Please wait...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Plane size={18} />
                  {isLogin ? 'Sign In' : 'Create Account'}
                </span>
              )}
            </button>
          </form>
          
          {/* Switch Mode */}
          <p className="text-center mt-6 text-gray-500">
            {isLogin ? "Don't have an account?" : "Already have an account?"}{' '}
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-teal-600 font-semibold hover:text-teal-500 transition-colors"
              data-testid="switch-mode-btn"
            >
              {isLogin ? 'Sign Up' : 'Sign In'}
            </button>
          </p>
        </div>
        
        {/* Privacy Note */}
        <p className="text-center text-gray-400 text-sm mt-6 px-4">
          {"By continuing, you agree to Panchi's Terms of Service and Privacy Policy."}
        </p>
      </motion.div>
    </div>
  );
}
